import dayjs from 'dayjs'

/**
 * 格式化工具
 */
export default {
  /**
   * 格式化金额
   * @param {number} amount - 金额
   * @param {string} currency - 货币符号
   * @param {number} decimals - 小数位数
   */
  formatMoney(amount, currency = '¥', decimals = 2) {
    if (amount === null || amount === undefined) return `${currency}0.00`
    
    const value = Number(amount)
    if (isNaN(value)) return `${currency}0.00`
    
    return currency + value.toFixed(decimals).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  },

  /**
   * 格式化数字（千位分隔）
   * @param {number} num - 数字
   * @param {number} decimals - 小数位数
   */
  formatNumber(num, decimals = 0) {
    if (num === null || num === undefined) return '0'
    
    const value = Number(num)
    if (isNaN(value)) return '0'
    
    return value.toFixed(decimals).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  },

  /**
   * 格式化百分比
   * @param {number} value - 值
   * @param {number} decimals - 小数位数
   */
  formatPercent(value, decimals = 2) {
    if (value === null || value === undefined) return '0%'
    
    const num = Number(value)
    if (isNaN(num)) return '0%'
    
    return (num * 100).toFixed(decimals) + '%'
  },

  /**
   * 格式化日期
   * @param {string|Date} date - 日期
   * @param {string} format - 格式
   */
  formatDate(date, format = 'YYYY-MM-DD') {
    if (!date) return ''
    return dayjs(date).format(format)
  },

  /**
   * 格式化时间
   * @param {string|Date} date - 日期
   * @param {string} format - 格式
   */
  formatDateTime(date, format = 'YYYY-MM-DD HH:mm:ss') {
    if (!date) return ''
    return dayjs(date).format(format)
  },

  /**
   * 格式化相对时间
   * @param {string|Date} date - 日期
   */
  formatRelativeTime(date) {
    if (!date) return ''
    
    const now = dayjs()
    const target = dayjs(date)
    const diff = now.diff(target, 'minute')
    
    if (diff < 1) return '刚刚'
    if (diff < 60) return `${diff}分钟前`
    if (diff < 1440) return `${Math.floor(diff / 60)}小时前`
    if (diff < 10080) return `${Math.floor(diff / 1440)}天前`
    
    return target.format('MM-DD HH:mm')
  },

  /**
   * 格式化文件大小
   * @param {number} bytes - 字节数
   * @param {number} decimals - 小数位数
   */
  formatFileSize(bytes, decimals = 2) {
    if (bytes === 0) return '0 B'
    if (!bytes) return ''
    
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(decimals)) + ' ' + sizes[i]
  },

  /**
   * 格式化时长
   * @param {number} seconds - 秒数
   */
  formatDuration(seconds) {
    if (!seconds) return '00:00'
    
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    const secs = Math.floor(seconds % 60)
    
    if (hours > 0) {
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
    }
    
    return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  },

  /**
   * 格式化手机号
   * @param {string} phone - 手机号
   */
  formatPhone(phone) {
    if (!phone) return ''
    return phone.replace(/(\d{3})\d{4}(\d{4})/, '$1****$2')
  },

  /**
   * 格式化身份证
   * @param {string} idCard - 身份证号
   */
  formatIdCard(idCard) {
    if (!idCard) return ''
    return idCard.replace(/(\d{4})\d{10}(\d{4})/, '$1**********$2')
  },

  /**
   * 格式化银行卡
   * @param {string} bankCard - 银行卡号
   */
  formatBankCard(bankCard) {
    if (!bankCard) return ''
    return bankCard.replace(/\d{12}(\d{4})/, '**** **** **** $1')
  },

  /**
   * 格式化姓名
   * @param {string} name - 姓名
   */
  formatName(name) {
    if (!name) return ''
    if (name.length === 2) return name[0] + '*'
    return name[0] + '*'.repeat(name.length - 2) + name[name.length - 1]
  },

  /**
   * 格式化地址
   * @param {string} address - 地址
   * @param {number} length - 保留长度
   */
  formatAddress(address, length = 10) {
    if (!address) return ''
    if (address.length <= length) return address
    return address.substring(0, length) + '...'
  },

  /**
   * 格式化JSON
   * @param {Object} json - JSON对象
   * @param {number} indent - 缩进空格数
   */
  formatJSON(json, indent = 2) {
    if (!json) return ''
    try {
      return JSON.stringify(json, null, indent)
    } catch {
      return ''
    }
  },

  /**
   * 格式化HTML（去除标签）
   * @param {string} html - HTML字符串
   */
  formatPlainText(html) {
    if (!html) return ''
    return html.replace(/<[^>]*>/g, '')
  },

  /**
   * 格式化URL参数
   * @param {Object} params - 参数对象
   */
  formatUrlParams(params) {
    if (!params) return ''
    return Object.keys(params)
      .map(key => encodeURIComponent(key) + '=' + encodeURIComponent(params[key]))
      .join('&')
  }
}